import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr, fmtMoney, API } from "../lib/api";
import { LOGO_URL, WHATSAPP_NUMBER } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Leaf, Fish, Egg, Sprout, Beef, Sparkles, ShoppingCart, Plus, Minus, Trash2, ArrowLeft, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const CAT_ICON = { Livestock: Beef, Meat: Beef, Fishery: Fish, Poultry: Egg, Processed: Sparkles, Crops: Sprout };
const CART_KEY = "modav_cart";

export function Shop() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState({ customer_name: "", customer_phone: "", customer_address: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/public/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const shown = cat === "All" ? products : products.filter((p) => p.category === cat);
  const count = cart.reduce((s, c) => s + c.quantity, 0);
  const total = cart.reduce((s, c) => s + c.quantity * c.unit_price, 0);

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { product_id: p.id, name: p.name, unit_price: p.price, unit: p.unit, image_file: p.image_file, quantity: 1 }];
    });
    toast.success(`${p.name} added to cart`);
  };
  const setQty = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const rm = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const checkout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error("Your cart is empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/orders", {
        ...info,
        items: cart.map((c) => ({ product_id: c.product_id, name: c.name, quantity: c.quantity, unit_price: c.unit_price })),
      });
      // Build WhatsApp message
      const lines = cart.map((c) => `• ${c.quantity} x ${c.name} — ${fmtMoney(c.quantity * c.unit_price)}`).join("\n");
      const msg = `Hello Modav Farms! I'd like to place an order.\n\nOrder: ${data.order_number}\n${lines}\n\nTotal: ${fmtMoney(data.total)}\n\nName: ${info.customer_name}\nPhone: ${info.customer_phone}\nAddress: ${info.customer_address}${info.note ? `\nNote: ${info.note}` : ""}`;
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
      setDone(data); setCart([]); setOpen(false);
      setInfo({ customer_name: "", customer_phone: "", customer_address: "", note: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1A1D1A]">
      <header className="sticky top-0 z-40 border-b border-[#E2E8F0] bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Modav Farms" className="h-11 w-11 rounded-full" />
            <span className="font-display text-lg font-extrabold tracking-tight">Modav Farms</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden items-center gap-1.5 text-sm font-medium text-[#5C665E] hover:text-[#2C5530] sm:flex"><ArrowLeft className="h-4 w-4" />Back to site</Link>
            <Button onClick={() => setOpen(true)} className="relative rounded-full bg-[#2C5530] hover:bg-[#214324]" data-testid="open-cart">
              <ShoppingCart className="mr-1.5 h-4 w-4" />Cart
              {count > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C25934] px-1 text-[11px] font-bold text-white" data-testid="cart-count">{count}</span>}
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Shop Fresh from the Farm</h1>
        <p className="mt-2 text-[#5C665E]">Order livestock, fish, snails, poultry and crops — checkout on WhatsApp.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-[#2C5530] text-white" : "bg-white text-[#5C665E] ring-1 ring-[#E2E8F0] hover:bg-[#EFF3EE]"}`}>{c}</button>
          ))}
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {shown.map((p) => {
            const Icon = CAT_ICON[p.category] || Leaf;
            return (
              <div key={p.id} className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid="shop-product">
                <div className="flex h-44 items-center justify-center bg-gradient-to-br from-[#EAF1EA] to-[#E4EEF6]">
                  {p.image_file ? <img src={`${API}/public/files/${p.image_file}`} alt={p.name} className="h-full w-full object-cover" /> : <Icon className="h-14 w-14 text-[#2C5530]/40" />}
                </div>
                <div className="p-4">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-[#1565C0]">{p.category}</span>
                  <h3 className="font-display text-base font-bold leading-tight">{p.name}</h3>
                  <p className="mt-1 line-clamp-2 text-xs text-[#5C665E]">{p.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="num font-display text-lg font-extrabold text-[#2C5530]">{fmtMoney(p.price)}<span className="text-xs font-medium text-[#5C665E]">/{p.unit}</span></p>
                    <Button size="sm" onClick={() => add(p)} className="rounded-full bg-[#2C5530] hover:bg-[#214324]" data-testid="add-to-cart"><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cart drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="flex w-full flex-col sm:max-w-md">
          <SheetHeader><SheetTitle className="font-display text-xl">Your Cart</SheetTitle>
            <SheetDescription>Review your items and check out via WhatsApp.</SheetDescription>
          </SheetHeader>
          <div className="-mx-6 flex-1 overflow-y-auto px-6 py-2">
            {cart.length === 0 ? (
              <p className="py-10 text-center text-sm text-[#5C665E]">Your cart is empty. Add some farm-fresh products!</p>
            ) : cart.map((c) => (
              <div key={c.product_id} className="flex items-center gap-3 border-b border-[#E2E8F0] py-3" data-testid="cart-line">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#EAF1EA]">
                  {c.image_file ? <img src={`${API}/public/files/${c.image_file}`} alt="" className="h-full w-full rounded-md object-cover" /> : <Leaf className="h-5 w-5 text-[#2C5530]/50" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{c.name}</p>
                  <p className="num text-xs text-[#5C665E]">{fmtMoney(c.unit_price)} × {c.quantity}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(c.product_id, -1)}><Minus className="h-3 w-3" /></Button>
                  <span className="num w-6 text-center text-sm">{c.quantity}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(c.product_id, 1)} data-testid="cart-inc"><Plus className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => rm(c.product_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-[#E2E8F0] pt-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium">Total</span>
                <span className="num font-display text-xl font-extrabold text-[#2C5530]" data-testid="cart-total">{fmtMoney(total)}</span>
              </div>
              <form onSubmit={checkout} className="space-y-2.5">
                <Input placeholder="Your name" value={info.customer_name} onChange={(e) => setInfo({ ...info, customer_name: e.target.value })} required data-testid="checkout-name" />
                <Input placeholder="Phone number" value={info.customer_phone} onChange={(e) => setInfo({ ...info, customer_phone: e.target.value })} required data-testid="checkout-phone" />
                <Input placeholder="Delivery address" value={info.customer_address} onChange={(e) => setInfo({ ...info, customer_address: e.target.value })} data-testid="checkout-address" />
                <Textarea placeholder="Note (optional)" rows={2} value={info.note} onChange={(e) => setInfo({ ...info, note: e.target.value })} />
                <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#25D366] py-6 text-base hover:bg-[#1da851]" data-testid="checkout-whatsapp">
                  {busy ? "Placing order…" : "Checkout via WhatsApp"}
                </Button>
              </form>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Success */}
      <Sheet open={!!done} onOpenChange={() => setDone(null)}>
        <SheetContent side="bottom" className="mx-auto max-w-md rounded-t-2xl text-center">
          <SheetHeader className="sr-only"><SheetTitle>Order placed</SheetTitle>
            <SheetDescription>Your order confirmation</SheetDescription></SheetHeader>
          <div className="py-6" data-testid="order-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h2 className="mt-4 font-display text-2xl font-extrabold">Order placed!</h2>
            <p className="mt-2 text-sm text-[#5C665E]">Your order <b>{done?.order_number}</b> ({fmtMoney(done?.total || 0)}) was sent. Please complete the chat on WhatsApp to confirm delivery & payment.</p>
            <Button className="mt-5 rounded-full bg-[#2C5530] hover:bg-[#214324]" onClick={() => setDone(null)}>Continue shopping</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
